import React from 'react';

// =============================================
// DASHBOARD SECTION COMPONENT
// =============================================
export function DashboardSection({
    baseCurrencyLabel,
    fmtBase,
    goldTotals,
    accountTotals,
    investmentTotals,
    fixedAssetTotals,
    loanTotals,
    cardTotals,
    goldInventory,
    accounts,
    investments,
    fixedAssets,
    personalLoans,
    goldLoans,
    creditCards,
    handleOpenScorecard,
    setActiveSection,
    setActiveModule
}) {
    return (
        <div className="section-container">

            {/* ── NG-3: Monthly Performance Scorecard Button ── */}
            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={handleOpenScorecard}
                    style={{
                        width: '100%',
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(245,158,11,0.1) 100%)',
                        border: '1px solid rgba(255,215,0,0.4)',
                        borderRadius: '14px',
                        color: '#FFD700',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        fontFamily: 'inherit',
                        transition: 'all 0.2s',
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>📊</span>
                        <span>This Month's Performance Report</span>
                    </span>
                    <span style={{ fontSize: '18px', opacity: 0.7 }}>›</span>
                </button>
            </div>
            <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h2 style={{ fontSize: '22px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
                        📊 Financial Overview
                    </h2>
                    <span style={{
                        background: 'rgba(255,215,0,0.12)', border: '1px solid rgba(255,215,0,0.3)',
                        borderRadius: '100px', padding: '4px 12px', fontSize: '12px',
                        fontWeight: '700', color: 'var(--primary)', letterSpacing: '0.5px',
                    }}>
                        {baseCurrencyLabel} base
                    </span>
                </div>
                <div className="summary-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                    {/* Assets Summary */}
                    <div className="summary-card" style={{ background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                        <div className="summary-card-title" style={{ color: '#4CAF50' }}>💎 Total Assets</div>
                        <div className="summary-stats">
                            <div className="summary-stat">
                                <div className="summary-stat-value" style={{ color: '#4CAF50', fontSize: '16px' }}>
                                    {fmtBase(goldTotals.totalBase + accountTotals.totalBase + investmentTotals.totalBase + fixedAssetTotals.totalBase, { compact: true })}
                                </div>
                                <div className="summary-stat-label">{baseCurrencyLabel}</div>
                            </div>
                            <div className="summary-stat">
                                <div className="summary-stat-value">{goldInventory.length + accounts.length + investments.length + fixedAssets.length}</div>
                                <div className="summary-stat-label">Items</div>
                            </div>
                        </div>
                    </div>

                    {/* Liabilities Summary */}
                    <div className="summary-card" style={{ background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)', border: '1px solid rgba(244, 67, 54, 0.3)' }}>
                        <div className="summary-card-title" style={{ color: '#F44336' }}>💳 Total Liabilities</div>
                        <div className="summary-stats">
                            <div className="summary-stat">
                                <div className="summary-stat-value" style={{ color: '#F44336', fontSize: '16px' }}>
                                    {fmtBase(loanTotals.personalRemaining + loanTotals.goldOwed + cardTotals.totalBase, { compact: true })}
                                </div>
                                <div className="summary-stat-label">{baseCurrencyLabel}</div>
                            </div>
                            <div className="summary-stat">
                                <div className="summary-stat-value">{personalLoans.length + goldLoans.length + creditCards.length}</div>
                                <div className="summary-stat-label">Active</div>
                            </div>
                        </div>
                    </div>

                    {/* Net Worth */}
                    {(() => {
                        const totalAssets = goldTotals.totalBase + accountTotals.totalBase + investmentTotals.totalBase + fixedAssetTotals.totalBase;
                        const totalLiab = loanTotals.personalRemaining + loanTotals.goldOwed + cardTotals.totalBase;
                        const netWorth = totalAssets - totalLiab;
                        const isPositive = netWorth >= 0;
                        return (
                            <div className="summary-card" style={{ background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                                <div className="summary-card-title" style={{ color: '#FFC107' }}>📈 Net Worth</div>
                                <div className="summary-stats">
                                    <div className="summary-stat">
                                        <div className="summary-stat-value" style={{ color: isPositive ? '#FFC107' : '#EF4444', fontSize: '16px' }}>
                                            {fmtBase(netWorth, { compact: true })}
                                        </div>
                                        <div className="summary-stat-label">{baseCurrencyLabel}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Detailed Breakdown */}
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: 'var(--text-primary)' }}>
                📋 Detailed Breakdown
            </h2>
            <div className="summary-grid">
                <div className="summary-card">
                    <div className="summary-card-title">💎 Gold Inventory</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value">{goldTotals.totalItems}</div>
                            <div className="summary-stat-label">Items</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{goldTotals.totalWeight.toFixed(1)}g</div>
                            <div className="summary-stat-label">Weight</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(goldTotals.totalBase, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Value</div>
                        </div>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="summary-card-title">🏦 Bank Accounts</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value">{accounts.length}</div>
                            <div className="summary-stat-label">Accounts</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(accountTotals.totalBase, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Total</div>
                        </div>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="summary-card-title">💰 Personal Loans</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value">{personalLoans.length}</div>
                            <div className="summary-stat-label">Active</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(loanTotals.personalTotal, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Total</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(loanTotals.personalRemaining, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Rem.</div>
                        </div>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="summary-card-title">🏆 Gold Pawning</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value">{goldLoans.length}</div>
                            <div className="summary-stat-label">Active</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(loanTotals.goldTotal, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Borrowed</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(loanTotals.goldOwed, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Owed</div>
                        </div>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="summary-card-title">💳 Credit Cards</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value">{creditCards.length}</div>
                            <div className="summary-stat-label">Cards</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fmtBase(cardTotals.totalBase, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Outstanding</div>
                        </div>
                    </div>
                </div>

                <div className="summary-card" style={{ cursor: 'pointer', border: '1px solid rgba(99,102,241,0.2)' }} onClick={() => { setActiveSection('investments'); setActiveModule('inv-portfolio'); }}>
                    <div className="summary-card-title" style={{ color: '#818cf8' }}>📈 Investments</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value" style={{ color: '#818cf8' }}>{fmtBase(investmentTotals.totalBase, { compact: true })}</div>
                            <div className="summary-stat-label">{baseCurrencyLabel} Total</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value" style={{ color: investmentTotals.pnl >= 0 ? '#10B981' : '#EF4444' }}>
                                {investmentTotals.pnl >= 0 ? '+' : ''}{fmtBase(investmentTotals.pnl, { compact: true })}
                            </div>
                            <div className="summary-stat-label">Unrealised P&L</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{investments.length}</div>
                            <div className="summary-stat-label">Positions</div>
                        </div>
                    </div>
                </div>

                <div className="summary-card" style={{ cursor: 'pointer', border: '1px solid rgba(251,191,36,0.2)' }} onClick={() => { setActiveSection('fixed-assets'); setActiveModule('fa-overview'); }}>
                    <div className="summary-card-title" style={{ color: '#fbbf24' }}>🏠 Fixed Assets</div>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <div className="summary-stat-value" style={{ color: '#fbbf24' }}>{fmtBase(fixedAssetTotals.totalBase, { compact: true })}</div>
                            <div className="summary-stat-label">CMV</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value" style={{ color: '#6ee7b7' }}>{fmtBase(fixedAssetTotals.fsvBase, { compact: true })}</div>
                            <div className="summary-stat-label">FSV</div>
                        </div>
                        <div className="summary-stat">
                            <div className="summary-stat-value">{fixedAssets.length}</div>
                            <div className="summary-stat-label">Assets</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
